import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useChatbotContext } from '../../../../context/ChatbotContext';
import { auth } from '../../../../firebase/config/config';
import axios from 'axios';
import logger from '../../../../utils/logger';
import { toast } from 'sonner';
import { X, Upload, Rocket, Edit2, ChevronRight, Globe, FileText, Bot, ArrowLeft } from 'lucide-react';

const ONBOARDING_THEMES = [
    {
        name: 'Monochrome',
        header: '#292929',
        chatBackground: '#FFFFFF',
        botMessageBox: '#F7F7F7',
        botMessageText: '#292929',
        userMessageBox: '#292929',
        userMessageText: '#F7F7F7',
    },
    {
        name: 'Modern Minimalist',
        header: '#454545',
        chatBackground: '#ecf0f1',
        botMessageBox: '#bdc3c7',
        botMessageText: '#2c3e50',
        userMessageBox: '#454545',
        userMessageText: '#ffffff',
    },
    {
        name: 'Autumn Harvest',
        header: '#8D6E63',
        chatBackground: '#EFEBE9',
        botMessageBox: '#D7CCC8',
        botMessageText: '#4E342E',
        userMessageBox: '#A1887F',
        userMessageText: '#FFFFFF',
    },
    {
        name: 'Corporate Elegance',
        header: '#003366',
        chatBackground: '#f2f2f2',
        botMessageBox: '#e6f0ff',
        botMessageText: '#003366',
        userMessageBox: '#879AC4',
        userMessageText: '#FFFFFF',
    },
    {
        name: 'Purple Dreams',
        header: '#6C63FF',
        chatBackground: '#F5F7FF',
        botMessageBox: '#D1D4FF',
        botMessageText: '#3F3D56',
        userMessageBox: '#6C63FF',
        userMessageText: '#FFFFFF',
    },
    {
        name: 'Dark Mode',
        header: '#2c3e50',
        chatBackground: '#1e272e',
        botMessageBox: '#576574',
        botMessageText: '#d2dae2',
        userMessageBox: '#3E4651',
        userMessageText: '#ffffff',
    },
    {
        name: 'Forest Canopy',
        header: '#435740',
        chatBackground: '#E8F5E9',
        botMessageBox: '#A5D6A7',
        botMessageText: '#1B5E20',
        userMessageBox: '#66BB6A',
        userMessageText: '#FFFFFF',
    },
    {
        name: 'Executive Dark',
        header: '#212729',
        chatBackground: '#212729',
        botMessageBox: '#2F3841',
        botMessageText: '#999999',
        userMessageBox: '#424242',
        userMessageText: '#999999',
    },
    {
        name: 'WhatsApp Classic',
        header: '#075E54',
        chatBackground: '#FFFFFF',
        botMessageBox: '#DCF8C6',
        botMessageText: '#1C1C1C',
        userMessageBox: '#075E54',
        userMessageText: '#FFFFFF',
    },
    {
        name: 'Mint Freshness',
        header: '#26A69A',
        chatBackground: '#EBFFFA',
        botMessageBox: '#A7FFEB',
        botMessageText: '#00695C',
        userMessageBox: '#00ACC1',
        userMessageText: '#FFFFFF',
    },
];

const BASE_URL = typeof window !== 'undefined' ? window.location.origin : '';
const PRESET_ICONS = [
    '/assets/Avatars/AV29.png',
    '/assets/Avatars/AV45.png',
    '/assets/Avatars/AV20.png',
    '/assets/Avatars/AV5.png',
    '/assets/Avatars/AV38.png',
    '/assets/Avatars/AV60.png',
    '/assets/Avatars/AV87.png',
    '/assets/Avatars/AV90.png',
    '/assets/Avatars/AV95.png',
    '/assets/ChatbotToggleButtons/Adriana O\'Sullivan.webp',
    '/assets/ChatbotToggleButtons/Aliah Lane.webp',
    '/assets/ChatbotToggleButtons/Alisa Hester.webp',
    '/assets/ChatbotToggleButtons/Amanda Lowery.webp',
    '/assets/ChatbotToggleButtons/Ammar Foley.webp',
    '/assets/ChatbotToggleButtons/Ashton Blackwell.webp',
    '/assets/ChatbotToggleButtons/Ava Bentley.webp',
    '/assets/ChatbotToggleButtons/Bailey Richards.webp',
    '/assets/ChatbotToggleButtons/Benedict Doherty.webp',
    '/assets/ChatbotToggleButtons/Blake Riley.webp',
    '/assets/ChatbotToggleButtons/Elisa Nishikawa.webp',
    '/assets/ChatbotToggleButtons/Ethan Valdez.webp',
    '/assets/ChatbotToggleButtons/Jonathan Kelly.webp',
    '/assets/ChatbotToggleButtons/Kari Rasmussen.webp',
    '/assets/ChatbotToggleButtons/Marco Gross.webp',
    '/assets/ChatbotToggleButtons/Owen Harding.webp',
    '/assets/ChatbotToggleButtons/Kaden Scott.webp',
    '/assets/ChatbotToggleButtons/Zaid Schwartz.webp',
].map(url => url.startsWith('http') ? url : `${BASE_URL}${url}`);

const CreateChatbotForm = ({ open, onClose, createdCallback }) => {
    const { resetToDefaultConfig, refreshChatbots } = useChatbotContext();
    const [step, setStep] = useState(1); // 1: Identity, 2: AI Behavior, 3: Confirm, 4: Launch

    // Identity State (Step 1)
    const [name, setName] = useState('My AI Assistant');
    const [headerColor, setHeaderColor] = useState(ONBOARDING_THEMES[0].header);
    const [iconUrl, setIconUrl] = useState(PRESET_ICONS[0]);
    const [customIconUrl, setCustomIconUrl] = useState('');
    const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);

    // AI Behavior State (Step 2 - NEW)
    const [websiteUrl, setWebsiteUrl] = useState('');
    const [agentName, setAgentName] = useState('');
    const [tone, setTone] = useState('Professional');
    const [responseLength, setResponseLength] = useState(100);
    const [additionalInstructions, setAdditionalInstructions] = useState('');

    // Auto-detected State (Step 3 - NEW)
    const [detectedCompanyName, setDetectedCompanyName] = useState('');
    const [detectedEmail, setDetectedEmail] = useState('');
    const [pagesFound, setPagesFound] = useState(0);
    const [crawledSitemap, setCrawledSitemap] = useState(''); // Store sitemap for reuse
    const [websiteContext, setWebsiteContext] = useState('');
    const [isAnalyzing, setIsAnalyzing] = useState(false);

    // Knowledge State (Step 4)
    const [trainingTab, setTrainingTab] = useState('website'); // 'website' or 'file'
    const [trainingUrl, setTrainingUrl] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);

    // Status State
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Initializing...');
    const [error, setError] = useState(null);

    // Reset on Open
    useEffect(() => {
        if (open) {
            const defaults = resetToDefaultConfig();
            setName(defaults.assistantName || 'My AI Assistant');
            setHeaderColor(ONBOARDING_THEMES[0].header);
            setIconUrl(PRESET_ICONS[0]);
            setCustomIconUrl('');
            // AI Behavior reset
            setWebsiteUrl('');
            setAgentName('');
            setTone('Professional');
            setResponseLength(100);
            setAdditionalInstructions('');
            // Auto-detected reset
            setDetectedCompanyName('');
            setDetectedEmail('');
            setPagesFound(0);
            setCrawledSitemap('');
            setWebsiteContext('');
            setIsAnalyzing(false);
            // Knowledge reset
            setTrainingTab('website');
            setTrainingUrl('');
            setSelectedFiles([]);
            setError(null);
            setStep(1);
            setLoading(false);
        }
    }, [open, resetToDefaultConfig]);

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        const validFiles = files.filter(file => file.size <= 20 * 1024 * 1024);

        if (validFiles.length < files.length) {
            setError('Some files were too large (max 20MB)');
        } else {
            setError(null);
        }

        if (validFiles.length > 0) {
            setSelectedFiles(prev => [...prev, ...validFiles]);
        }
    };

    const handleNext = async () => {
        setError(null);
        if (step === 1) {
            // Step 1 → 2: Identity → AI Behavior
            if (!name.trim()) { setError("Please give your assistant a name."); return; }
            // Pre-fill agent name from assistant name if empty
            if (!agentName) setAgentName(name);
            setStep(2);
        } else if (step === 2) {
            // Step 2 → 3: AI Behavior → Confirmation
            // Analyze website to auto-detect company name and email
            if (websiteUrl) {
                setIsAnalyzing(true);
                try {
                    const response = await axios.post('/api/chatbot/analyze-website', { url: websiteUrl });
                    if (response.data) {
                        setDetectedCompanyName(response.data.companyName || '');
                        setDetectedEmail(response.data.email || '');
                        setPagesFound(response.data.pagesFound || 0);
                        setCrawledSitemap(response.data.sitemap || ''); // Store for reuse
                        setWebsiteContext(response.data.websiteContext || '');
                    }
                } catch (err) {
                    logger.error('Failed to analyze website:', err);
                    // Continue anyway, user can fill manually
                }
                setIsAnalyzing(false);
            }
            // Also use website URL for training if not set
            if (websiteUrl && !trainingUrl) {
                setTrainingUrl(websiteUrl);
            }
            setStep(3);
        } else if (step === 3) {
            // Step 3 → 4: Confirmation → Launch
            setStep(4);
        }
    };

    const handleBack = () => {
        setError(null);
        if (step > 1) setStep(step - 1);
    };

    const handleLaunch = async () => {
        setLoading(true);
        setError(null);
        setLoadingMessage('Creating Agent Key...');

        try {
            const user = auth.currentUser;
            if (!user) throw new Error('User not authenticated');

            const chatbotId = uuidv4();
            const defaultConfig = resetToDefaultConfig();

            // Find the selected theme based on headerColor
            const selectedTheme = ONBOARDING_THEMES.find(t => t.header === headerColor) || {};

            // 2. Upload File if present
            let uploadedFileIds = [];
            if (trainingTab === 'file' && selectedFiles.length > 0) {
                setLoadingMessage(`Uploading ${selectedFiles.length} Documents...`);
                const formData = new FormData();
                selectedFiles.forEach(file => {
                    formData.append('files', file);
                });

                const uploadRes = await axios.post('/api/files/upload', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'x-user-id': user.uid
                    },
                });

                if (uploadRes.data.uploadedFiles) {
                    uploadedFileIds = uploadRes.data.uploadedFiles.map(f => f.id.toString());
                }
            }

            // 3. Generate AI System Prompt (if we have behavior settings)
            let generatedPrompt = defaultConfig.chatbotRolePrompt;
            const promptUrl = websiteUrl || (trainingTab === 'website' ? trainingUrl : '');
            console.log('[CreateChatbot] Starting prompt generation...', { promptUrl, detectedCompanyName });
            if (promptUrl || detectedCompanyName) {
                setLoadingMessage('Generating AI Instructions...');
                try {
                    const promptRes = await axios.post('/api/chatbot/generate-system-prompt', {
                        url: promptUrl,
                        businessName: detectedCompanyName,
                        agentName: agentName || name,
                        tone: tone,
                        responseLength: responseLength,
                        email: detectedEmail,
                        userConstraints: additionalInstructions ? additionalInstructions.split('\n').filter(Boolean) : [],
                        sitemap: promptUrl === websiteUrl ? crawledSitemap : '',
                        websiteContext: promptUrl === websiteUrl ? websiteContext : ''
                    });
                    console.log('[CreateChatbot] Prompt response:', promptRes.data);
                    if (!promptRes.data?.prompt) throw new Error('The instruction generator returned no prompt. Please retry.');
                    generatedPrompt = promptRes.data.prompt;
                    console.log('[CreateChatbot] ✓ Generated prompt length:', generatedPrompt.length);
                    logger.log('Generated comprehensive system prompt');
                } catch (promptErr) {
                    console.error('[CreateChatbot] ✗ Prompt generation failed:', promptErr);
                    logger.error('Failed to generate custom prompt:', promptErr);
                    throw new Error(promptErr?.response?.data?.error || promptErr.message || 'Could not generate website instructions. Please retry.');
                }
            }
            console.log('[CreateChatbot] Final prompt (first 200 chars):', generatedPrompt.substring(0, 200));

            // 4. Prepare Config - Merge default config with selected theme and generated prompt
            const newConfig = {
                ...defaultConfig,
                assistantName: name,
                botIconUrl: customIconUrl || iconUrl,
                trainingUrl: trainingUrl || websiteUrl,
                chatbotRolePrompt: generatedPrompt, // Use generated prompt - CORRECT FIELD NAME
                // Apply theme colors
                headerColor: headerColor,
                chatBackgroundColor: selectedTheme.chatBackground || defaultConfig.chatBackgroundColor,
                botMessageBoxColor: selectedTheme.botMessageBox || defaultConfig.botMessageBoxColor,
                botMessageTextColor: selectedTheme.botMessageText || defaultConfig.botMessageTextColor,
                userMessageBoxColor: selectedTheme.userMessageBox || defaultConfig.userMessageBoxColor,
                userMessageTextColor: selectedTheme.userMessageText || defaultConfig.userMessageTextColor,
            };

            // 5. Create/Save Chatbot
            setLoadingMessage('Configuring Agent...');
            const response = await fetch('/api/chatbot', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${await user.getIdToken()}`
                },
                body: JSON.stringify({
                    chatbotId,
                    config: newConfig,
                    userId: user.uid
                }),
            });

            if (!response.ok) throw new Error('Failed to create chatbot');

            // 4. Assign Files
            if (uploadedFileIds.length > 0) {
                await axios.post(`/api/chatbot/${chatbotId}/files`, {
                    fileIds: uploadedFileIds
                });
            }

            // 5. Start a plan-capped crawl. The Website tab tracks this job.
            if (trainingTab === 'website' && trainingUrl) {
                setLoadingMessage('Starting Website Training...');
                try {
                    const token = await user.getIdToken();
                    await axios.post('/api/files/crawl/start', {
                        url: trainingUrl,
                        chatbotId
                    }, {
                        headers: {
                            'x-user-id': user.uid,
                            'Authorization': `Bearer ${token}`,
                        }
                    });
                } catch (crawlErr) {
                    logger.error('Error during automatic website crawling:', crawlErr);
                    await refreshChatbots();
                    toast.error(`Assistant created, but website training could not start: ${crawlErr?.response?.data?.error || 'Please retry from the Website tab.'}`);
                    onClose();
                    return;
                }
            }

            // 6. Refresh List
            setLoadingMessage('Finalizing...');
            await refreshChatbots();

            if (trainingTab === 'website' && trainingUrl) {
                toast.success('Assistant created. Website training is running; check the Website tab for progress.');
            } else {
                toast.success('Assistant created.');
            }

            if (createdCallback) createdCallback();
            onClose();

        } catch (err) {
            logger.error('Error launching chatbot:', err);
            setError(err.message || 'Failed to launch chatbot');
            setLoading(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden relative flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="px-8 py-6 border-b border-slate-100 flex justify-between items-center bg-white z-10">
                    <div>
                        <h2 className="text-xl font-bold text-slate-800">New Assistant</h2>
                        <div className="flex gap-2 mt-2">
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${step >= i ? 'w-8 bg-violet-600' : 'w-2 bg-slate-200'}`}></div>
                            ))}
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Content Body */}
                <div className="p-8 overflow-y-auto flex-1">
                    {/* Error Message */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-50 text-red-600 text-sm rounded-xl border border-red-100 flex items-center gap-2">
                            <span className="font-bold">Error:</span> {error}
                        </div>
                    )}

                    {/* Step 1: Identity */}
                    {step === 1 && (
                        <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
                            <div className="text-center mb-8">
                                <h3 className="text-lg font-bold text-slate-800 mb-1">Who is this agent?</h3>
                                <p className="text-slate-500 text-sm">Give your assistant a face and a name.</p>
                            </div>

                            <div className="flex flex-col items-center gap-6">
                                {/* Avatar Picker */}
                                <div className="relative group">
                                    <div className="w-24 h-24 rounded-2xl overflow-hidden border-2 border-slate-100 shadow-sm bg-slate-50 group-hover:border-violet-200 transition-colors">
                                        <img src={customIconUrl || iconUrl} alt="Avatar" className="w-full h-full object-cover" />
                                    </div>
                                    <button
                                        onClick={() => setIsAvatarMenuOpen(!isAvatarMenuOpen)}
                                        className="absolute -bottom-2 -right-2 p-2 bg-slate-800 text-white rounded-full shadow-md hover:scale-105 transition-transform"
                                    >
                                        <Edit2 size={14} />
                                    </button>

                                    {/* Avatar Menu Popup */}
                                    {isAvatarMenuOpen && (
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-64 bg-white rounded-xl shadow-xl border border-slate-100 p-4 z-20">
                                            <div className="grid grid-cols-4 gap-2 mb-3">
                                                {PRESET_ICONS.map((url) => (
                                                    <button
                                                        key={url}
                                                        onClick={() => { setIconUrl(url); setCustomIconUrl(''); setIsAvatarMenuOpen(false); }}
                                                        className={`w-10 h-10 rounded-lg overflow-hidden border-2 transition-all ${iconUrl === url ? 'border-violet-500 ring-2 ring-violet-500/20' : 'border-transparent hover:border-slate-200'}`}
                                                    >
                                                        <img src={url} alt="Preset" className="w-full h-full object-cover" />
                                                    </button>
                                                ))}
                                            </div>
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    placeholder="Or paste image URL..."
                                                    className="w-full text-xs p-2 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-violet-500"
                                                    value={customIconUrl}
                                                    onChange={(e) => setCustomIconUrl(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Name Input */}
                                <div className="w-full">
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Name</label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full text-lg font-medium p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-500/10 focus:border-violet-500 transition-all placeholder:text-slate-300"
                                        placeholder="e.g. Sales Assistant"
                                        autoFocus
                                    />
                                </div>

                                {/* Theme Selection */}
                                <div className="w-full">
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Theme Style</label>
                                    <div className="flex gap-4 overflow-x-auto p-4 -mx-4 snap-x scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
                                        {ONBOARDING_THEMES.map(theme => (
                                            <button
                                                key={theme.header}
                                                onClick={() => setHeaderColor(theme.header)}
                                                className={`group relative flex flex-col text-left rounded-lg transition-all duration-200 focus:outline-none min-w-[124px] w-[124px] snap-start ${headerColor === theme.header ? 'ring-2 ring-violet-500 ring-offset-2' : 'hover:scale-105'}`}
                                            >
                                                {/* Theme Preview Card */}
                                                <div className="w-full h-24 rounded-lg pointer-events-none select-none border border-slate-200 overflow-hidden relative flex flex-col" style={{ backgroundColor: theme.chatBackground }}>
                                                    {/* Header */}
                                                    <div className="w-full h-4 shrink-0" style={{ backgroundColor: theme.header }}></div>

                                                    {/* Chat Area */}
                                                    <div className="flex-1 p-2 flex flex-col gap-2 justify-center">
                                                        {/* Bot Message */}
                                                        <div className="flex w-full justify-start">
                                                            <div className="w-8 h-4 rounded-md rounded-tl-sm" style={{ backgroundColor: theme.botMessageBox }}></div>
                                                        </div>
                                                        {/* User Message */}
                                                        <div className="flex w-full justify-end">
                                                            <div className="w-8 h-4 rounded-md rounded-tr-sm" style={{ backgroundColor: theme.userMessageBox }}></div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="mt-1.5 text-center">
                                                    <span className={`block text-[10px] font-semibold truncate ${headerColor === theme.header ? 'text-violet-600' : 'text-slate-600'}`}>
                                                        {theme.name}
                                                    </span>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: AI Behavior */}
                    {step === 2 && (
                        <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
                            <div className="text-center mb-6">
                                <h3 className="text-lg font-bold text-slate-800 mb-1">How should it behave?</h3>
                                <p className="text-slate-500 text-sm">Configure your AI's personality and responses.</p>
                            </div>

                            {/* Website URL */}
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Website URL</label>
                                <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl p-3 focus-within:ring-2 focus-within:ring-violet-500/10 focus-within:border-violet-500 transition-all">
                                    <Globe size={18} className="text-slate-400 mr-3" />
                                    <input
                                        type="url"
                                        value={websiteUrl}
                                        onChange={(e) => {
                                            const nextUrl = e.target.value;
                                            setTrainingUrl(current => current === websiteUrl ? nextUrl : current);
                                            setWebsiteUrl(nextUrl);
                                            setDetectedCompanyName('');
                                            setDetectedEmail('');
                                            setPagesFound(0);
                                            setCrawledSitemap('');
                                            setWebsiteContext('');
                                        }}
                                        placeholder="https://yourwebsite.com"
                                        className="flex-1 bg-transparent outline-none text-sm text-slate-800 placeholder:text-slate-300"
                                    />
                                </div>
                                <p className="text-xs text-slate-400 mt-1.5">We'll analyze your site to customize responses.</p>
                            </div>

                            {/* Tone Selection - Pill Style */}
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Tone</label>
                                <div className="flex flex-wrap gap-2">
                                    {['Professional', 'Friendly', 'Enthusiastic', 'Empathetic', 'Strict'].map(t => (
                                        <button
                                            key={t}
                                            type="button"
                                            onClick={() => setTone(t)}
                                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tone === t
                                                ? 'bg-violet-600 text-white shadow-sm'
                                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                }`}
                                        >
                                            {t}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Response Length Slider */}
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Max Response Length</label>
                                <div className="px-2">
                                    <input
                                        type="range"
                                        min="50"
                                        max="200"
                                        step="25"
                                        value={responseLength}
                                        onChange={(e) => setResponseLength(Number(e.target.value))}
                                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-violet-600"
                                    />
                                    <div className="flex justify-between text-xs text-slate-400 mt-1">
                                        <span>Short</span>
                                        <span className={`font-medium ${responseLength === 100 ? 'text-violet-600' : 'text-slate-500'}`}>
                                            {responseLength} words {responseLength === 100 && '(Recommended)'}
                                        </span>
                                        <span>Detailed</span>
                                    </div>
                                </div>
                            </div>

                            {/* Additional Instructions */}
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Additional Instructions</label>
                                <textarea
                                    value={additionalInstructions}
                                    onChange={(e) => setAdditionalInstructions(e.target.value)}
                                    placeholder="e.g. Don't discuss competitor products&#10;Always recommend our consultation service"
                                    className="w-full text-sm p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-500/10 focus:border-violet-500 min-h-[80px] resize-none transition-all placeholder:text-slate-300"
                                />
                            </div>
                        </div>
                    )}

                    {/* Step 3: Confirmation */}
                    {step === 3 && (
                        <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
                            {isAnalyzing ? (
                                <div className="text-center py-12">
                                    <div className="w-16 h-16 relative mx-auto mb-4">
                                        <div className="absolute inset-0 border-4 border-slate-100 rounded-full"></div>
                                        <div className="absolute inset-0 border-4 border-violet-500 rounded-full border-t-transparent animate-spin"></div>
                                        <Globe className="absolute inset-0 m-auto text-violet-500" size={20} />
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-800 mb-1">Analyzing Website...</h3>
                                    <p className="text-slate-500 text-sm">Extracting business info and sitemap</p>
                                </div>
                            ) : (
                                <>
                                    <div className="text-center mb-6">
                                        <h3 className="text-lg font-bold text-slate-800 mb-1">Confirm Details</h3>
                                        <p className="text-slate-500 text-sm">We detected these from your website. Please verify.</p>
                                    </div>

                                    {pagesFound > 0 && (
                                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4 flex items-center gap-3">
                                            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                                                <Globe className="text-emerald-600" size={18} />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-emerald-800">{pagesFound} pages discovered</p>
                                                <p className="text-xs text-emerald-600">Sitemap ready for AI navigation</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Company Name */}
                                    <div>
                                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Company Name</label>
                                        <input
                                            type="text"
                                            value={detectedCompanyName}
                                            onChange={(e) => setDetectedCompanyName(e.target.value)}
                                            placeholder="Your Company Name"
                                            className="w-full text-sm p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-500/10 focus:border-violet-500 transition-all"
                                        />
                                    </div>

                                    {/* Support Email */}
                                    <div>
                                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Support Email</label>
                                        <input
                                            type="email"
                                            value={detectedEmail}
                                            onChange={(e) => setDetectedEmail(e.target.value)}
                                            placeholder="support@yourcompany.com"
                                            className="w-full text-sm p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-500/10 focus:border-violet-500 transition-all"
                                        />
                                    </div>

                                    {/* Summary */}
                                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mt-4">
                                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">AI Configuration Summary</h4>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-slate-500">Agent Name</span>
                                                <span className="font-medium text-slate-700">{agentName || name}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-slate-500">Tone</span>
                                                <span className="font-medium text-slate-700">{tone}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-slate-500">Response Length</span>
                                                <span className="font-medium text-slate-700">{responseLength} words</span>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Step 4: Knowledge & Launch */}
                    {step === 4 && (
                        <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
                            <div className="text-center mb-8">
                                <h3 className="text-lg font-bold text-slate-800 mb-1">What does it know?</h3>
                                <p className="text-slate-500 text-sm">Choose a primary knowledge source to start training.</p>
                            </div>

                            <div className="flex p-1 bg-slate-100/80 rounded-xl mb-6">
                                <button
                                    onClick={() => setTrainingTab('website')}
                                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${trainingTab === 'website' ? 'bg-white text-violet-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <Globe size={16} />
                                    Website
                                </button>
                                <button
                                    onClick={() => setTrainingTab('file')}
                                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${trainingTab === 'file' ? 'bg-white text-violet-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <FileText size={16} />
                                    Document
                                </button>
                            </div>

                            {trainingTab === 'website' ? (
                                <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                                    {trainingUrl ? (
                                        /* URL already configured from Step 2 */
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                <Globe className="text-emerald-600" size={22} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-slate-700">Website configured</p>
                                                <p className="text-xs text-slate-500 truncate">{trainingUrl}</p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setTrainingUrl('')}
                                                className="text-xs text-slate-400 hover:text-slate-600 px-2 py-1 rounded hover:bg-slate-200"
                                            >
                                                Change
                                            </button>
                                        </div>
                                    ) : (
                                        /* No URL yet - show input */
                                        <>
                                            <label className="block text-sm font-medium text-slate-700 mb-2">Website URL</label>
                                            <div className="flex items-center bg-white border border-slate-200 rounded-lg p-3 focus-within:ring-2 focus-within:ring-violet-500/10 focus-within:border-violet-500 transition-all">
                                                <Globe size={18} className="text-slate-400 mr-3" />
                                                <input
                                                    type="url"
                                                    value={trainingUrl}
                                                    onChange={(e) => setTrainingUrl(e.target.value)}
                                                    placeholder="https://yourwebsite.com"
                                                    className="flex-1 outline-none text-sm text-slate-800 placeholder:text-slate-300"
                                                />
                                            </div>
                                            <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                                                <Bot size={12} />
                                                We'll automatically crawl and train on this site.
                                            </p>
                                        </>
                                    )}
                                </div>
                            ) : (
                                <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer bg-slate-50 hover:bg-violet-50 hover:border-violet-300 transition-all group overflow-hidden relative">
                                    <div className="flex flex-col items-center justify-center pt-5 pb-6 w-full px-4">
                                        <div className="p-3 bg-white rounded-full shadow-sm mb-3 group-hover:scale-110 transition-transform">
                                            <Upload className="w-6 h-6 text-violet-500" />
                                        </div>
                                        {selectedFiles.length > 0 ? (
                                            <div className="w-full text-center">
                                                <p className="mb-1 text-sm text-slate-700 font-semibold">
                                                    {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
                                                </p>
                                                <div className="flex flex-wrap gap-1 justify-center mt-1 opacity-70">
                                                    {selectedFiles.slice(0, 3).map((f, i) => (
                                                        <span key={i} className="text-[10px] bg-slate-200 px-1.5 py-0.5 rounded text-slate-600 truncate max-w-[100px]">{f.name}</span>
                                                    ))}
                                                    {selectedFiles.length > 3 && <span className="text-[10px] text-slate-500">+{selectedFiles.length - 3} more</span>}
                                                </div>
                                                <p className="text-xs text-violet-500 mt-2 font-medium">Click to add more</p>
                                            </div>
                                        ) : (
                                            <>
                                                <p className="mb-1 text-sm text-slate-500 group-hover:text-violet-600 font-medium">
                                                    Click to upload PDF or TXT
                                                </p>
                                                <p className="text-xs text-slate-400">PDF, TXT, DOCX up to 20MB</p>
                                            </>
                                        )}
                                    </div>
                                    <input type="file" className="hidden" onChange={handleFileChange} accept=".pdf,.txt,.docx" multiple />
                                </label>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer Controls (For Steps 1-3) */}
                {step < 4 && (
                    <div className="px-8 py-6 border-t border-slate-100 bg-slate-50/50 flex justify-between items-center">
                        {step > 1 ? (
                            <button
                                onClick={handleBack}
                                className="flex items-center gap-2 text-slate-500 hover:text-slate-700 font-medium px-4 py-2 rounded-lg hover:bg-slate-200/50 transition-colors"
                            >
                                <ArrowLeft size={16} />
                                Back
                            </button>
                        ) : (
                            <div></div> // Spacer
                        )}

                        <button
                            onClick={handleNext}
                            disabled={isAnalyzing}
                            className="flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white px-6 py-2.5 rounded-xl font-bold transition-all shadow-lg shadow-violet-600/20 active:scale-95 disabled:opacity-50"
                        >
                            {isAnalyzing ? 'Analyzing...' : 'Continue'}
                            <ChevronRight size={18} />
                        </button>
                    </div>
                )}

                {/* Footer Controls (For Step 4 - Launch) */}
                {step === 4 && (
                    <div className="px-8 py-6 border-t border-slate-100 bg-slate-50/50 flex justify-between items-center">
                        <button
                            onClick={handleBack}
                            disabled={loading}
                            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 font-medium px-4 py-2 rounded-lg hover:bg-slate-200/50 transition-colors disabled:opacity-50"
                        >
                            <ArrowLeft size={16} />
                            Back
                        </button>

                        {loading ? (
                            <div className="flex items-center gap-3">
                                <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
                                <span className="text-sm text-slate-500">{loadingMessage}</span>
                            </div>
                        ) : (
                            <button
                                onClick={handleLaunch}
                                className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-6 py-2.5 rounded-xl font-bold transition-all shadow-lg shadow-slate-900/20 active:scale-95"
                            >
                                <Rocket size={18} />
                                Launch Now
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div >
    );
};

export default CreateChatbotForm;
